import { getApiUrl } from "../config";

export type ExportHistoryItem = {
  id: number;
  addresses: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  filePath: string;
  filename: string;
  fileName?: string | null;
};

export type ExportHistoryResponse = {
  exports: ExportHistoryItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

export const fetchExportHistory = async (page = 1, limit = 10): Promise<ExportHistoryResponse> => {
  const res = await fetch(getApiUrl(`/api/exports/history?page=${page}&limit=${limit}`), {
    credentials: "include"
  });
  
  if (!res.ok) {
    throw new Error('Failed to fetch export history');
  }
  
  return res.json();
};

export const downloadExport = async (exportId: number): Promise<void> => {
  const res = await fetch(getApiUrl(`/api/exports/${exportId}/download`), {
    credentials: "include"
  });
  
  if (!res.ok) {
    throw new Error('Failed to download export');
  }
  
  // Get filename from Content-Disposition header
  const contentDisposition = res.headers.get('content-disposition');
  let filename = 'export.zip';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }
  
  // Create blob and download
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const deleteExport = async (exportId: number): Promise<void> => {
  const res = await fetch(getApiUrl(`/api/exports/${exportId}`), {
    method: 'DELETE',
    credentials: "include"
  });

  if (!res.ok) {
    throw new Error('Failed to delete export');
  }
};

// Async export job types and functions

export type CreateExportJobParams = {
  addresses: string[];
  startDate: string | null;
  endDate: string | null;
  columns?: string;
  fileName: string | null;
  source: "wallet_exports" | "batch_exports";
};

export type ExportJobStatus = {
  id: number;
  status: "pending" | "processing" | "completed" | "failed";
  progress: {
    completed: number;
    total: number;
  };
  exportHistoryId: number | null;
  errorMessage: string | null;
};

export const createExportJob = async (params: CreateExportJobParams): Promise<{ jobId: number; status: string }> => {
  const res = await fetch(getApiUrl("/api/exports/jobs"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    const errorData = await res.json();
    const error = new Error(errorData.error || "Failed to create export job") as any;
    error.status = res.status;
    error.data = errorData;
    throw error;
  }

  return res.json();
};

export const getExportJobStatus = async (jobId: number): Promise<ExportJobStatus> => {
  const res = await fetch(getApiUrl(`/api/exports/jobs/${jobId}`), {
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to fetch job status");
  }

  return res.json();
};